import {
    CSSProperties,
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import { IVideoSliderItemProps, IVideoSliderProps } from "./VideoSlider.interface";
import { TranslationContext } from "../../../shared/providers/translation/translation.provider";
import { ModuleContext } from "../../../shared/providers/module/module.provider";
import { CONTENT_BASE_URL } from "../../../shared/constants";
import { ComponentTypes } from "../../../shared/interfaces/modules.interface";
import Placeholder from "../../../shared/assets/imgs/placeholder.svg";
import Player, { TimeEvent } from "@vimeo/player";
import Button from "../../Button/Button";
import { useIntersectionObserver } from "../../../shared/hooks/useIntersectionObserver";
import { delay } from "../../../shared/helpers/utils";
import { useDrag } from "../../../shared/hooks/useDrag";
import { IVimeo } from "../../../shared/interfaces/vimeo.interface";
import { useVimeo } from "../../../shared/hooks/useVimeo";
import { LoadingContext } from "../../../shared/providers/loading/loading.provider";

function VideoSlider(props: IVideoSliderProps) {
    const { lang, translate } = useContext(TranslationContext);
    const slider = useRef<HTMLDivElement>(null);
    const [currentVideo, setCurrentVideo] = useState<number>(0);
    const [visibleSlide, setVisibleSlide] = useState<number>(0);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [completedVideos, setCompletedVideos] = useState<string[]>([]);
    const [videosProgress, setVideosProgress] = useState<{ [index: string]: number }>();
    const { setProgress } = useContext(ModuleContext);

    const parseId = useCallback((url: string) => {
        const params = url.split("/").pop();
        const [id] = params?.split("#t=") ?? [];
        return id;
    }, []);

    useEffect(() => {
        if (lang && completedVideos.length >= props.videos.length)
            setProgress({
                unitId: props.unitId,
                itemId: props.id,
                progress: 100,
                itemType: ComponentTypes.VIDEO_SLIDER,
                itemLocale: lang
            });
    }, [lang, completedVideos]);

    useEffect(() => {
        if (!videosProgress?.[currentVideo] || videosProgress?.[currentVideo] < 1) return;

        const nextIndex = currentVideo + 1;
        if (nextIndex >= props.videos.length) {
            setIsFullscreen(false);
        } else {
            setCurrentVideo(nextIndex);
        }
    }, [videosProgress]);

    useEffect(() => {
        const checkVisibility = () => {
            if (document.visibilityState === "visible") return;
            setIsFullscreen(false);
        };
        document.addEventListener("visibilitychange", checkVisibility);

        return () => document.removeEventListener("visibilitychange", checkVisibility);
    }, []);

    const { ref: fullscreenBtns } = useDrag({ onDragDown: () => setIsFullscreen(false) });

    return (
        <section className="unit_item no_margins container-fluid gx-2 py-2 pe-0 bg-yellow-100 ">
            <div className="row gx-0">
                <div className="col-12">
                    <h2 className="text-h4-bd mb-1">{props.title}</h2>
                    <p className="text-bd3-rg pe-1 color-neutrals-darker">{props.content}</p>
                    <div className="slider_video" ref={slider}>
                        <div className="slider_video-stage">
                            {props.videos.map((video, i) => (
                                <VideoItem
                                    key={i}
                                    {...props}
                                    index={i}
                                    currentVideo={currentVideo}
                                    setCurrentVideo={setCurrentVideo}
                                    isFullscreen={isFullscreen}
                                    setIsFullscreen={setIsFullscreen}
                                    videoId={parseId(video.videoUrl)}
                                    thumbnail={video.thumbnail}
                                    setCompletedVideos={setCompletedVideos}
                                    setVideosProgress={setVideosProgress}
                                    setVisibleSlide={setVisibleSlide}
                                />
                            ))}
                        </div>
                        <div className="fullscreen_back">
                            <Button
                                type="button"
                                icon="Arrow--left"
                                iconSize={16}
                                customCss="text-bd2-rg color-neutrals-white gap-02"
                                label={translate("common.back", "Back")}
                                onClick={() => setIsFullscreen(false)}
                            />
                        </div>
                        <div className="fullscreen_nav">
                            <div className="fullscreen_header">
                                <div className="indicators align-items-center">
                                    {props.videos.map((_, i) => (
                                        <span
                                            style={
                                                {
                                                    "--progress": `${i === currentVideo ? videosProgress?.[i] : 1}`
                                                } as CSSProperties
                                            }
                                            className={`indicator ${i <= currentVideo ? "is_active" : ""} `}
                                            key={i}></span>
                                    ))}
                                </div>
                            </div>
                            <div className="fullscreen_btns" ref={fullscreenBtns}>
                                {currentVideo >= 0 && (
                                    <button
                                        type="button"
                                        className="previous"
                                        onClick={() => {
                                            const prevIndex = currentVideo - 1;
                                            if (prevIndex < 0) {
                                                setIsFullscreen(false);
                                            } else {
                                                setCurrentVideo(prevIndex);
                                            }
                                        }}></button>
                                )}
                                {currentVideo < props.videos.length && (
                                    <button
                                        type="button"
                                        className="next"
                                        onClick={() => {
                                            const nextIndex = currentVideo + 1;
                                            if (nextIndex >= props.videos.length) {
                                                setIsFullscreen(false);
                                            } else {
                                                setCurrentVideo(nextIndex);
                                            }
                                        }}></button>
                                )}
                            </div>
                        </div>
                        <div className="slider_image-navigation">
                            {props.videos.map((_, i) => {
                                if (i === 0) return <Fragment key={i} />;
                                return (
                                    <span
                                        data-index={i}
                                        key={i}
                                        className={`navigation_item ${i === visibleSlide ? "is_active" : ""}`}></span>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default VideoSlider;

const VideoItem = (props: IVideoSliderItemProps) => {
    const {
        index,
        thumbnail,
        title,
        videoId,
        isFullscreen,
        setIsFullscreen,
        currentVideo,
        setCurrentVideo,
        setCompletedVideos,
        setVideosProgress,
        setVisibleSlide
    } = props;
    const { translate } = useContext(TranslationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const ref = useRef<HTMLVideoElement>(null);
    const [video, setVideo] = useState<HTMLVideoElement>();
    const [isCompleted, setIsCompleted] = useState<boolean>(false);
    const { data: vimeoVideo, isLoading } = useVimeo(videoId);

    useEffect(() => {
        setIsLoading(isLoading);
    }, [isLoading]);

    useEffect(() => {
        const videoPlayer = ref.current;
        if (!videoPlayer) return;

        videoPlayer.addEventListener("timeupdate", (e: Event) => {
            const video = e.target as HTMLVideoElement;
            const duration = video.duration ?? 1;
            const percent = video.currentTime / duration;

            setIsCompleted(percent >= 0.9);
            setVideosProgress((p: any) => ({ ...p, [index]: percent }));
        });

        setVideo(videoPlayer);
    }, [ref]);

    useEffect(() => {
        if (isCompleted) {
            setCompletedVideos((v: string) => [...v, videoId]);
        }
    }, [isCompleted]);

    const removeFullscreen = (video: HTMLVideoElement) => {
        const videoPlayer = ref.current;

        if (!videoPlayer) return;
        videoPlayer.classList.add("d-none");
        videoPlayer.classList.remove("is_full");
        video.pause();
    };

    useEffect(() => {
        if (isFullscreen) document.body.classList.add("overflow-hidden");
        else document.body.classList.remove("overflow-hidden");
    }, [isFullscreen]);

    const handlePlay = async () => {
        try {
            const videoPlayer = ref.current;
            if (!videoPlayer || !video) return;

            history.pushState(null, document.title, window.location.href);
            videoPlayer.classList.remove("d-none");
            videoPlayer.classList.add("is_full");

            video.currentTime = 0;
            video.volume = 1;

            if (vimeoVideo?.success) await video.play();

            setCurrentVideo(index);
            setIsFullscreen(true);

            const onGoBack = () => {
                history.pushState(null, document.title, window.location.href);
                removeFullscreen(video);
                setIsFullscreen(false);
                window.removeEventListener("popstate", onGoBack);
            };

            const onEsc = (event: any) => {
                if (event.key !== "Escape") return;
                removeFullscreen(video);
                setIsFullscreen(false);
            };

            document.addEventListener("keydown", onEsc);
            window.addEventListener("popstate", onGoBack);
        } catch (e) {
            console.log(e);
        }
    };

    useEffect(() => {
        // PAUSE VIDEO ON LEAVE FULLSCREEN
        if (!video || isFullscreen) return;
        removeFullscreen(video);
    }, [video, isFullscreen]);

    useEffect(() => {
        // PLAY ON VIDEO CHANGE WHEN FULLSCREEN
        if (!video || !isFullscreen) return;

        if (currentVideo !== index) removeFullscreen(video);
        else handlePlay();
    }, [currentVideo, index, isFullscreen]);

    const handleIntersection = async (e: any) => {
        if (!e.isIntersecting) return;

        setVisibleSlide(index);
    };

    const { ref: thumbnailRef } = useIntersectionObserver(handleIntersection);

    if (!videoId) return <></>;

    return (
        <div className="video_wrapper">
            <button type="button" onClick={handlePlay}>
                <img
                    ref={thumbnailRef}
                    className="video_thumb"
                    src={thumbnail ? `${CONTENT_BASE_URL}${thumbnail.url}` : Placeholder}
                    aria-label={translate("common.play", "Play")}
                />
            </button>
            <div className="fullscreen_video">
                <video
                    ref={ref}
                    src={vimeoVideo?.success ? vimeoVideo.data.link : undefined}
                    className="item_video d-none"
                    title={title}
                    playsInline
                    controlsList="nodownload"
                />
            </div>
        </div>
    );
};
